import React, { useEffect, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { TextField } from "@components/FormsV2";
import Select2V2 from "@components/FormsV2/Select2/Select2V2";
import CountryPicker from "@components/Forms/CountryPicker";
import DropdownApiJs from "@components/FormsV2/DropdownApiJs/DropdownApiJs";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import NestedItemSelect, {
  Section,
} from "@components/NestedItemSelect/NestedItemSelect";
import useProjectOverviewData from "../../pages/Projects/ProjectOverviewV2/api/useProjectOverviewData";
import useGetProject from "../CreateBlueprintFromProjectModal/api/useGetProject";
import {
  buildGeneralSection,
  buildPsSection,
  buildMsSection,
  buildVendorQuotesSection,
  buildGovernanceSection,
  buildProductSection,
  buildResourcesSection,
  buildPricingSection,
  buildPayload,
} from "../CreateBlueprintFromProjectModal/projectSections";
import { Project } from "../CreateBlueprintFromProjectModal/types/Project";
import useFlags from "@common/hooks/useFlags";
import {
  useListClientsForAccountQuery,
  useV2CreateProjectDuplicationMutation,
} from "@generated";
import AutoCompleteText from "@components/Forms/AutoCompleteText/AutoCompleteText";
import "./style.css";

const GENERAL_OPTIONS = [
  "customer-summary",
  "contacts",
  "terms-and-conditions",
  "teammates",
  "attachments",
  "notes",
];

const PRICING_OPTIONS = [
  "one-time-revenue-adjustments",
  "recurring-revenue-adjustments",
  "payment-credits",
];

const buildSections = (project: Project | null): Section[] => [
  buildGeneralSection(GENERAL_OPTIONS),
  buildPsSection(project),
  buildMsSection(project),
  buildGovernanceSection(project),
  buildVendorQuotesSection(project),
  buildProductSection(project),
  buildResourcesSection(project),
  buildPricingSection(PRICING_OPTIONS),
];

interface Props {
  projectId: string;
  show: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

// Duplication runs in a background job, so a successful response only means the
// request was accepted — not that the new project exists yet.
export const DUPLICATE_PROJECT_QUEUED_MESSAGE =
  "Duplication request submitted. The new project will appear in your project list in a moment.";

const EMPTY_FORM = {
  crmOption: null as any,
  projectName: "",
  salesExecutiveId: "",
  presalesEngineerId: "",
  serviceLocation: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  country: "" as any,
  paymentTermId: "",
  rateTableId: "",
  businessUnitId: "",
};

const EMPTY_CLIENT = { id: null as string | null, name: "" };

const DuplicateProjectModal: React.FC<Props> = ({
  projectId,
  show,
  onClose,
  onSuccess,
}) => {
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const {
    accountSlug,
    projectData,
    includedClient,
    includedSalesExec,
    includedPresales,
    includedBusinessUnit,
    includedPaymentTerm,
    includedRateTable,
    projectLocations,
    salesExecOptions,
    presalesOptions,
    paymentTermOptions,
    rateTableOptions,
    businessUnitOptions,
    isCrm,
    fieldLabels,
    isLoading,
  } = useProjectOverviewData(projectId);
  const {
    configCrmOpportunityRequired,
    configSalesExecutiveRequired,
    configPresalesEngineerRequired,
    configBusinessUnitRequired,
    configServiceLocationRequired,
  } = useFlags();

  const { project, isLoading: isProjectItemsLoading } =
    useGetProject(projectId);
  const [duplicateProject, { isLoading: isSubmitting }] =
    useV2CreateProjectDuplicationMutation();

  const labels = {
    crmOpportunity: fieldLabels["crm_opportunity"] || "CRM Opportunity",
    salesExecutive: fieldLabels["sales_executive"] || "Sales Executive",
    presalesEngineer: fieldLabels["presales_engineer"] || "Pre-Sales Engineer",
    client: fieldLabels["client"] || "Client",
    location: fieldLabels["location"] || "Service Location",
    paymentTerm: fieldLabels["payment_term"] || "Payment Term",
    businessUnit: fieldLabels["business_unit"] || "Business Unit",
  };

  const [selectedClient, setSelectedClient] = useState({ ...EMPTY_CLIENT });
  const { data: clientsData } = useListClientsForAccountQuery(
    { slug: accountSlug, filter: { active: true, name: selectedClient.name } },
    { skip: !accountSlug }
  );
  const clients = (clientsData?.data ?? []).map((c: any) => ({
    id: String(c.id),
    name: ((c.attributes as any)?.name ?? "") as string,
  }));
  const clientList = clients.map((c) => c.name);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [sections, setSections] = useState<Section[]>(() =>
    buildSections(null)
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">(
    "success"
  );

  useEffect(() => {
    if (show && !isLoading && projectData) {
      const loc = (projectLocations[0] as any) ?? null;
      setSelectedClient({
        id: String((includedClient as any)?.id ?? "") || null,
        name: (includedClient as any)?.attributes?.name ?? "",
      });
      setForm({
        crmOption: null,
        projectName: (projectData?.attributes as any)?.["project-name"] ?? "",
        salesExecutiveId: String(includedSalesExec?.id ?? ""),
        presalesEngineerId: String(includedPresales?.id ?? ""),
        serviceLocation: loc?.attributes?.name ?? "",
        addressLine1: loc?.attributes?.street ?? "",
        addressLine2: loc?.attributes?.street2 ?? "",
        city: loc?.attributes?.city ?? "",
        state: loc?.attributes?.state ?? "",
        zip: loc?.attributes?.["postal-code"] ?? "",
        country: loc?.attributes?.country ?? "",
        paymentTermId: String(includedPaymentTerm?.id ?? ""),
        rateTableId: String(includedRateTable?.id ?? ""),
        businessUnitId: String(includedBusinessUnit?.id ?? ""),
      });
    }
  }, [show, isLoading]);

  useEffect(() => {
    if (show && !isProjectItemsLoading && project)
      setSections(buildSections(project));
  }, [show, isProjectItemsLoading]);

  const set = (key: keyof typeof EMPTY_FORM) => (val: any) =>
    setForm((f) => ({ ...f, [key]: val }));

  const handleContinue = () => {
    const missing: string[] = [];
    if (isCrm && configCrmOpportunityRequired && !form.crmOption)
      missing.push(labels.crmOpportunity);
    if (!form.projectName.trim()) missing.push("Project Name");
    if (configSalesExecutiveRequired && !form.salesExecutiveId)
      missing.push(labels.salesExecutive);
    if (configPresalesEngineerRequired && !form.presalesEngineerId)
      missing.push(labels.presalesEngineer);
    if (!selectedClient.name.trim()) missing.push(labels.client);
    if (configServiceLocationRequired && !form.serviceLocation.trim())
      missing.push(labels.location);
    if (!form.paymentTermId) missing.push(labels.paymentTerm);
    if (!form.rateTableId) missing.push("Rate Table");
    if (configBusinessUnitRequired && !form.businessUnitId)
      missing.push(labels.businessUnit);
    if (missing.length > 0) {
      setAlertVariant("danger");
      setAlertMessage(`Required fields missing: ${missing.join(", ")}`);
      return;
    }
    setAlertMessage(null);
    setStep(2);
  };

  const handleSubmit = async () => {
    /* This buildPayload function is also used in CreateBlueprintFromProjectModal and creates 
    an array of JSON API records from the sections in Step 2. The additional logic that comes after this
    function is for defining the project attributes/relationships specific to Duplicating Project (Step 1) 
    that are not present in CreateBlueprintFromProject */
    const payload = buildPayload(sections, project);

    // Ensure the project resource exists at the front of the payload
    let projectResource = payload.find((item: any) => item.type === "projects");
    if (!projectResource) {
      projectResource = { type: "projects", id: Number(projectId) };
      payload.unshift(projectResource);
    }

    // Merge step 1 form attributes
    projectResource.attributes = {
      ...(projectResource.attributes ?? {}),
      "project-name": form.projectName,
      "client-name": selectedClient.name,
    };

    // Merge step 1 form relationships
    const formRelationships: Record<string, any> = {};
    for (const [rel, id] of [
      ["sales-executive", form.salesExecutiveId],
      ["presales-engineer", form.presalesEngineerId],
      ["payment-term", form.paymentTermId],
      ["rate-table", form.rateTableId],
      ["business-unit", form.businessUnitId],
      ["client", selectedClient.id || ""],
    ]) {
      if (id)
        formRelationships[rel] = { data: { id: Number(id), type: `${rel}s` } };
    }
    if (form.crmOption?.value && form.crmOption.value !== -1)
      formRelationships["crm-opportunity"] = {
        data: { id: Number(form.crmOption.value), type: "crm-opportunities" },
      };

    projectResource.relationships = {
      ...(projectResource.relationships ?? {}),
      ...formRelationships,
    };

    // Add project-location record to payload
    if (form.serviceLocation || form.addressLine1 || form.city) {
      const existingLocationId = (projectLocations[0] as any)?.id;
      const locationRecord: any = {
        type: "project-locations",
        attributes: {
          name: form.serviceLocation,
          street: form.addressLine1,
          street2: form.addressLine2,
          city: form.city,
          state: form.state,
          "postal-code": form.zip,
          country: form.country,
        },
      };
      if (existingLocationId) locationRecord.id = Number(existingLocationId);
      payload.push(locationRecord);
    }

    try {
      const data = {
        type: "project-duplications",
        attributes: { content: payload },
        relationships: {
          "source-project": {
            data: { id: projectId, type: "projects" },
          },
        },
      };
      const response = await duplicateProject({
        projectId: projectId,
        body: { data },
      }).unwrap();
      if (response?.data?.id) {
        handleClose();
        onSuccess(DUPLICATE_PROJECT_QUEUED_MESSAGE);
      }
    } catch (e: any) {
      setAlertVariant("danger");
      setAlertMessage(
        e?.data?.errors?.[0]?.detail ?? "Failed to duplicate project."
      );
    }
  };

  const handleClose = () => {
    setStep(1);
    setForm({ ...EMPTY_FORM });
    setSelectedClient({ ...EMPTY_CLIENT });
    setSections(buildSections(null));
    setAlertMessage(null);
    onClose();
  };

  const renderStep1 = () => (
    <>
      <div className="sectionHeader">1. Project Details</div>
      <div className="description">
        Enter the details relevant to your organization in regards to the
        project.
      </div>
      {isCrm && (
        <Row>
          <Col sm={12}>
            <DropdownApiJs
              value={form.crmOption}
              setValue={set("crmOption")}
              placeholder={`Select ${labels.crmOpportunity}`}
              defaultValue={{ value: -1, label: "-- None --" }}
              onChange={set("crmOption")}
              onInputChange={null}
              required={configCrmOpportunityRequired}
              label={`${configCrmOpportunityRequired ? "*" : ""}${
                labels.crmOpportunity
              }`}
              className=""
              isInvalid={false}
              onFocus={null}
              url={`${apiHost}/${accountSlug}/v1/crm-opportunities`}
              isDisabled={false}
              filter="display-name"
              blankOption={{ value: -1, label: "-- None --" }}
              canCreate={false}
              setDataState={null}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={12}>
          <TextField
            id="projectName"
            label="*Project Name"
            placeholder="Enter Project Name"
            value={form.projectName}
            onChange={(e) => set("projectName")(e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <Select2V2
            label={`${configSalesExecutiveRequired ? "*" : ""}${
              labels.salesExecutive
            }`}
            placeholder={`Select ${labels.salesExecutive}`}
            value={
              salesExecOptions.find(
                (o) => String(o.value) === form.salesExecutiveId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("salesExecutiveId")(opt?.value ? String(opt.value) : "")
            }
            options={salesExecOptions}
            isClearable={!configSalesExecutiveRequired}
          />
        </Col>
        <Col sm={6}>
          <Select2V2
            label={`${configPresalesEngineerRequired ? "*" : ""}${
              labels.presalesEngineer
            }`}
            placeholder={`Select ${labels.presalesEngineer}`}
            value={
              presalesOptions.find(
                (o) => String(o.value) === form.presalesEngineerId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("presalesEngineerId")(opt?.value ? String(opt.value) : "")
            }
            options={presalesOptions}
            isClearable={!configPresalesEngineerRequired}
          />
        </Col>
      </Row>

      <div className="sectionHeader">2. Client Details</div>
      <div className="description">
        Enter the details relevant to the client for whom you are rendering
        services.
      </div>
      <Row>
        <Col sm={12}>
          <AutoCompleteText
            required
            id="clientName"
            label={`*${labels.client}`}
            placeholder={`Enter ${labels.client}`}
            suggestionList={clientList}
            value={selectedClient.name}
            monitorValue={(value: string) => {
              const match = clients.find((c) => c.name === value);
              setSelectedClient({ id: match ? match.id : null, name: value });
            }}
            setTextChanged={() => {}}
            crmId={null}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <TextField
            id="serviceLocation"
            label={`${configServiceLocationRequired ? "*" : ""}${
              labels.location
            }`}
            placeholder={`Enter ${labels.location}`}
            value={form.serviceLocation}
            onChange={(e) => set("serviceLocation")(e.target.value)}
          />
        </Col>
      </Row>
      <Form.Group className="formGroup">
        <Form.Label>Address</Form.Label>
        <TextField
          id="addressLine1"
          label={null}
          placeholder="Address line 1"
          value={form.addressLine1}
          onChange={(e) => set("addressLine1")(e.target.value)}
        />
        <TextField
          id="addressLine2"
          label={null}
          placeholder="Address line 2"
          value={form.addressLine2}
          onChange={(e) => set("addressLine2")(e.target.value)}
        />
      </Form.Group>
      <Row>
        <Col sm={3}>
          <TextField
            id="city"
            label={null}
            placeholder="City"
            value={form.city}
            onChange={(e) => set("city")(e.target.value)}
          />
        </Col>
        <Col sm={3}>
          <TextField
            id="state"
            label={null}
            placeholder="State / Prov / Terr"
            value={form.state}
            onChange={(e) => set("state")(e.target.value)}
          />
        </Col>
        <Col sm={3}>
          <TextField
            id="zip"
            label={null}
            placeholder="Zip / Post Code"
            value={form.zip}
            onChange={(e) => set("zip")(e.target.value)}
          />
        </Col>
        <Col sm={3}>
          <CountryPicker
            country={form.country}
            onCountryChange={set("country")}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <Select2V2
            label={`*${labels.paymentTerm}`}
            placeholder={`Select ${labels.paymentTerm}`}
            value={
              paymentTermOptions.find(
                (o) => String(o.value) === form.paymentTermId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("paymentTermId")(opt?.value ? String(opt.value) : "")
            }
            options={paymentTermOptions}
          />
        </Col>
        <Col sm={6}>
          <Select2V2
            label="*Rate Table"
            placeholder="Select Rate Table"
            value={
              rateTableOptions.find(
                (o) => String(o.value) === form.rateTableId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("rateTableId")(opt?.value ? String(opt.value) : "")
            }
            options={rateTableOptions}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <Select2V2
            label={`${configBusinessUnitRequired ? "*" : ""}${
              labels.businessUnit
            }`}
            placeholder={`Select ${labels.businessUnit}`}
            value={
              businessUnitOptions.find(
                (o) => String(o.value) === form.businessUnitId
              ) ?? null
            }
            onChange={(opt: any) =>
              set("businessUnitId")(opt?.value ? String(opt.value) : "")
            }
            options={businessUnitOptions}
            isClearable={!configBusinessUnitRequired}
          />
        </Col>
      </Row>
    </>
  );

  const renderStep2 = () =>
    isProjectItemsLoading ? (
      <ScopeStackSpinner />
    ) : (
      <>
        <div className="sectionHeader">3. Include Project Sections</div>
        <div className="description">
          Choose which portions of this project to include in duplicated
          project.
        </div>
        <NestedItemSelect sections={sections} onSectionsChange={setSections} />
      </>
    );

  return (
    <>
      {alertMessage && (
        <ToastAlert
          variant={alertVariant}
          text={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      <ScopeStackModal
        className="duplicateProjectModal"
        show={show}
        modalTitle="Duplicate Project"
        modalBody={
          isLoading ? (
            <ScopeStackSpinner />
          ) : step === 1 ? (
            renderStep1()
          ) : (
            renderStep2()
          )
        }
        button1Text="Cancel"
        button2Text={
          step === 1
            ? "Continue"
            : isSubmitting
            ? "Duplicating..."
            : "Duplicate Project"
        }
        handleClose={handleClose}
        handleButton1Click={handleClose}
        handleButton2Click={step === 1 ? handleContinue : handleSubmit}
        button2Disabled={isSubmitting}
      />
    </>
  );
};

export default DuplicateProjectModal;
